import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messageAPI, userAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { 
  MessageSquare, Send, Plus, Search, Users, User, 
  MoreVertical, Trash2, X, Check
} from 'lucide-react';
import './Messages.css';

function Messages() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch conversations
  const { data: conversationsData, isLoading: loadingConversations } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => messageAPI.getConversations().then(res => res.data),
    refetchInterval: 10000, // Poll every 10 seconds
  });

  // Fetch messages for selected conversation
  const { data: messagesData, isLoading: loadingMessages } = useQuery({
    queryKey: ['messages', selectedConversation?.id],
    queryFn: () => messageAPI.getMessages(selectedConversation.id).then(res => res.data),
    enabled: !!selectedConversation,
    refetchInterval: 5000, // Poll every 5 seconds
  });

  // Fetch unread count
  const { data: unreadData } = useQuery({
    queryKey: ['unread-count'],
    queryFn: () => messageAPI.getUnreadCount().then(res => res.data),
    refetchInterval: 10000,
  });

  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: ({ conversationId, content }) => 
      messageAPI.sendMessage(conversationId, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries(['messages', selectedConversation?.id]);
      queryClient.invalidateQueries(['conversations']);
      setNewMessage('');
    },
  });

  // Create direct conversation mutation
  const createDirectMutation = useMutation({
    mutationFn: (userId) => messageAPI.getOrCreateDirect(userId),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['conversations']);
      setSelectedConversation(response.data.conversation);
      setShowNewConversation(false);
    },
  });

  const conversations = conversationsData?.conversations || [];
  const messages = messagesData?.messages || [];

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedConversation) {
      sendMutation.mutate({ 
        conversationId: selectedConversation.id, 
        content: newMessage 
      });
    }
  };

  const getConversationTitle = (conv) => {
    if (conv.name || conv.title) return conv.name || conv.title;
    const otherParticipants = conv.participants?.filter(p => p.user_id !== user?.id);
    if (otherParticipants?.length > 0) {
      return otherParticipants.map(p => `${p.first_name} ${p.last_name}`).join(', ');
    }
    return 'Conversation';
  };

  const getConversationAvatar = (conv) => {
    const otherParticipants = conv.participants?.filter(p => p.user_id !== user?.id);
    if (otherParticipants?.[0]?.profile_photo_url) {
      return otherParticipants[0].profile_photo_url;
    }
    return null;
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="messages-page">
      {/* Conversations Sidebar */}
      <div className="conversations-sidebar">
        <div className="sidebar-header">
          <h2><MessageSquare size={22} /> Messages</h2>
          <button className="new-conv-btn" onClick={() => setShowNewConversation(true)}>
            <Plus size={20} />
          </button>
        </div>
        
        <div className="conversations-list">
          {loadingConversations ? (
            <div className="loading-conv">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="no-conversations">
              <p>No conversations yet</p>
              <button onClick={() => setShowNewConversation(true)}>
                Start a conversation
              </button>
            </div>
          ) : (
            conversations.map(conv => (
              <div 
                key={conv.id}
                className={`conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div className="conv-avatar">
                  {getConversationAvatar(conv) ? (
                    <img src={getConversationAvatar(conv)} alt="" />
                  ) : (
                    <div className="avatar-placeholder">
                      {conv.type === 'group' ? <Users size={18} /> : getConversationTitle(conv).charAt(0)}
                    </div>
                  )}
                </div>
                <div className="conv-info">
                  <div className="conv-header">
                    <span className="conv-name">{getConversationTitle(conv)}</span>
                    {conv.last_message_at && (
                      <span className="conv-time">{formatMessageTime(conv.last_message_at)}</span>
                    )}
                  </div>
                  {conv.last_message && (
                    <p className="conv-preview">{conv.last_message}</p>
                  )}
                </div>
                {conv.unread_count > 0 && (
                  <span className="unread-badge">{conv.unread_count}</span>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="messages-area">
        {!selectedConversation ? (
          <div className="no-selection">
            <MessageSquare size={48} />
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the sidebar or start a new one</p>
          </div>
        ) : (
          <>
            <div className="messages-header">
              <div className="conv-avatar">
                {getConversationAvatar(selectedConversation) ? (
                  <img src={getConversationAvatar(selectedConversation)} alt="" />
                ) : (
                  <div className="avatar-placeholder">
                    {selectedConversation.type === 'group' ? 
                      <Users size={18} /> : 
                      getConversationTitle(selectedConversation).charAt(0)
                    }
                  </div>
                )}
              </div>
              <div className="header-info">
                <h3>{getConversationTitle(selectedConversation)}</h3>
                {selectedConversation.participants && (
                  <span className="participant-count">
                    {selectedConversation.participants.length} participants
                  </span>
                )}
              </div>
            </div>

            <div className="messages-container">
              {loadingMessages ? (
                <div className="loading-messages">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  <p>No messages yet. Say hello! 👋</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isOwn = msg.sender_id === user?.id;
                  const showAvatar = index === 0 || 
                    messages[index - 1].sender_id !== msg.sender_id;
                  
                  return (
                    <div 
                      key={msg.id} 
                      className={`message ${isOwn ? 'own' : 'other'}`}
                    >
                      {!isOwn && showAvatar && (
                        <div className="message-avatar">
                          {msg.sender_photo ? (
                            <img src={msg.sender_photo} alt="" />
                          ) : (
                            <div className="avatar-placeholder">
                              {msg.sender_name?.charAt(0) || 'U'}
                            </div>
                          )}
                        </div>
                      )}
                      <div className={`message-bubble ${!showAvatar && !isOwn ? 'no-avatar' : ''}`}>
                        {!isOwn && showAvatar && (
                          <span className="sender-name">{msg.sender_name}</span>
                        )}
                        <p className="message-text">{msg.content}</p>
                        <span className="message-time">
                          {formatMessageTime(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="message-input-area">
              <input
                type="text"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <button 
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sendMutation.isPending}
              >
                <Send size={20} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <NewConversationModal
          onClose={() => setShowNewConversation(false)}
          onSelectUser={(userId) => createDirectMutation.mutate(userId)}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
}

// New Conversation Modal
function NewConversationModal({ onClose, onSelectUser, currentUserId }) {
  const [search, setSearch] = useState('');
  
  // Fetch users matching search
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users-search', search],
    queryFn: () => userAPI.searchUsers(search).then(res => res.data),
    enabled: search.length > 0,
  });

  const users = usersData?.users || [];
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content new-conv-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Message</h2>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="search-users">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search for a person..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="users-list">
            {isLoading ? (
              <div className="loading">Searching...</div>
            ) : search.length === 0 ? (
              <p className="hint">Start typing to search for team members</p>
            ) : users.length === 0 ? (
              <p className="hint">No users found matching "{search}"</p>
            ) : (
              users.map(user => (
                <div 
                  key={user.id} 
                  className="user-item"
                  onClick={() => onSelectUser(user.id)}
                >
                  <div className="user-avatar">
                    {user.profile_photo_url ? (
                      <img src={user.profile_photo_url} alt={user.first_name} />
                    ) : (
                      <User size={18} />
                    )}
                  </div>
                  <div className="user-info">
                    <div className="user-name">
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="user-email">{user.email}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Messages;
